import React, { useState, useEffect, useCallback } from 'react';
import ModalPortal from '../UI/ModalPortal';
import { X, Calendar, Users, TrendingUp, Clock, AlertCircle, RefreshCw, ArrowRight, Briefcase, GraduationCap, Target } from 'lucide-react';
import statisticsService from '../../services/statisticsService';
import { getTalentTypeLabel } from '../../utils/talentTypeUtils';
import Avatar from '../UI/Avatar';
import { Loader } from '../UI/LoadingSpinner';
import { Pagination } from '@anyemp/ui-kit';
import StatisticsTable from './StatisticsTable';

const TYPE_CONFIG = {
  candidate: { icon: GraduationCap, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20' },
  employee:  { icon: Briefcase,      color: 'text-blue-600 dark:text-blue-400',   bg: 'bg-blue-50 dark:bg-blue-900/20'   },
  presale:   { icon: Target,         color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20' },
};

const getTypeConfig = (type) =>
  TYPE_CONFIG[type] || { icon: Users, color: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-50 dark:bg-gray-800' };

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const RECENT_TALENTS_COLUMNS = [
  {
    key: 'full_name',
    label: 'Name',
    render: (talent) => (
      <a href={`/talents/${talent.id}`} target="_blank" rel="noopener noreferrer"
        className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 visited:text-purple-700 dark:visited:text-purple-400 hover:underline font-medium">
        {talent.full_name}
      </a>
    ),
  },
  {
    key: 'talent_type',
    label: 'Type',
    render: (talent) => {
      const cfg = getTypeConfig(talent.talent_type);
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.color}`}>
          {getTalentTypeLabel(talent.talent_type)}
        </span>
      );
    },
  },
  {
    key: 'created_at',
    label: 'Added',
    render: (talent) => (
      <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {formatDate(talent.created_at)}
      </span>
    ),
  },
];

const ManagerDetailsModal = ({ managerId, filters, onClose }) => {
  const [details, setDetails] = useState(null);
  const talentRowKey = useCallback((talent) => talent.id, []);
  const [summary, setSummary] = useState(null); // кеш агрегатов (by_type, conversions, timeline)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
    setSummary(null);
  }, [managerId, filters.startDate, filters.endDate, filters.talent_types]);

  useEffect(() => {
    fetchDetails();
  }, [managerId, filters.startDate, filters.endDate, filters.talent_types, page]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const isFirstPage = page === 1;
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        talent_types: filters.talent_types,
        page,
        limit: 20,
        ...(isFirstPage ? {} : { page_only: true, total_hint: summary?.total_talents ?? summary?.pagination?.total })
      };
      const result = await statisticsService.getManagerDetails(managerId, params);
      if (result.data) {
        if (isFirstPage) {
          setSummary(result.data);
        }
        setDetails(result.data);
      }
    } catch (err) {
      console.error('Error fetching manager details:', err);
      setError(err.message || 'Failed to load manager details');
    } finally {
      setLoading(false);
    }
  };


  const modalShell = (content) => (
    <ModalPortal onClose={onClose} ariaLabel="Manager details">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-4rem)] flex flex-col overflow-hidden">
        {content}
      </div>
    </ModalPortal>
  );

  if (loading) {
    return modalShell(<>
      <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 dark:border-gray-700 sm:px-6">
        <span className="font-semibold text-gray-800 dark:text-white">Manager Details</span>
        <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="py-16 flex justify-center">
        <Loader variant="component" size="md" fullHeight={false} />
      </div>
    </>);
  }

  if (error) {
    return modalShell(<>
      <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 dark:border-gray-700 sm:px-6">
        <span className="font-semibold text-gray-800 dark:text-white">Manager Details</span>
        <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="px-6 py-10 flex flex-col items-center gap-4 text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
          <AlertCircle className="h-6 w-6 text-red-500 dark:text-red-400" />
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-white mb-1">Failed to load data</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={fetchDetails} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium">
            <RefreshCw className="h-4 w-4" /> Retry
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium">
            Close
          </button>
        </div>
      </div>
    </>);
  }

  if (!details) return null;

  const managerUser = details.manager?.user || details.manager || null;
  const managerName = managerUser?.fullName || managerUser?.name || `Manager ID: ${details.manager_id}`;
  const managerEmail = managerUser?.email;
  const managerAvatar = managerUser?.avatar;

  return (
    <ModalPortal onClose={onClose} ariaLabel="Manager details">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-4rem)] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 dark:border-gray-700 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar avatar={managerAvatar} name={managerName} size="md" />
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 dark:text-white truncate">{managerName}</p>
              {managerEmail && <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{managerEmail}</p>}
            </div>
          </div>
          <button onClick={onClose} className="ml-4 flex-shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto px-4 py-5 space-y-5 sm:px-6">

          {/* Stat Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2 sm:col-span-1 bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
              <Users className="h-5 w-5 text-primary-500 mb-2" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{(summary || details).total_talents || 0}</p>
              <p className="text-xs text-primary-600 dark:text-primary-400 font-medium mt-0.5">Total Talents</p>
            </div>
            {(summary || details).by_type && Object.entries((summary || details).by_type).map(([type, count]) => {
              const cfg = getTypeConfig(type);
              const Icon = cfg.icon;
              return (
                <div key={type} className={`rounded-lg p-4 ${cfg.bg}`}>
                  <Icon className={`h-5 w-5 mb-2 ${cfg.color}`} />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{count}</p>
                  <p className={`text-xs font-medium mt-0.5 ${cfg.color}`}>{getTalentTypeLabel(type)}</p>
                </div>
              );
            })}
          </div>

          {/* Conversions */}
          {((summary || details).conversions?.to_employee > 0 || (summary || details).conversions?.to_presale > 0) && (
            <div className="flex items-center gap-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-4 py-3">
              <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              <div className="flex flex-wrap gap-4">
                {(summary || details).conversions?.to_employee > 0 && (
                  <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                    <ArrowRight className="h-3.5 w-3.5 text-emerald-500" />
                    <strong>{(summary || details).conversions.to_employee}</strong> Employees
                  </span>
                )}
                {(summary || details).conversions?.to_presale > 0 && (
                  <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                    <ArrowRight className="h-3.5 w-3.5 text-emerald-500" />
                    <strong>{(summary || details).conversions.to_presale}</strong> Presales
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          {(summary || details).timeline && (summary || details).timeline.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> Addition Timeline
              </h3>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 max-h-48 overflow-y-auto">
                {(summary || details).timeline.map((item, index) => (
                  <div key={index} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      {formatDate(item.date)}
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-gray-400">{getTalentTypeLabel(item.talent_type)}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{item.count} {item.count === 1 ? 'talent' : 'talents'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Talents */}
          {details.recent_talents && details.recent_talents.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                Recently Added Talents
              </h3>
              <StatisticsTable
                columns={RECENT_TALENTS_COLUMNS}
                data={details.recent_talents}
                rowKey={talentRowKey}
                variant="ghost"
                footer={details?.pagination && details.pagination.total_pages > 1 ? (
                  <Pagination
                    currentPage={page}
                    totalPages={details.pagination.total_pages}
                    totalItems={details.pagination.total}
                    itemsPerPage={20}
                    onPageChange={setPage}
                    showResults="range"
                  />
                ) : null}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ManagerDetailsModal;
